import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { WIDGET_DOCS_URL, WIDGET_ROUTES_DOCS_URL } from './widgetSdkLinks';
import { WIDGET_API_SCOPES } from './widgetDataAccess';
import '../../styles/Widgets.css';

/** Connecting to APIs, shown inline on the wizard's Info step — not a popup. Leads with the two
 *  things people actually come here for (Momentum AMP's own data vs. an outside API), kept short
 *  on purpose: this used to also force-render the entire README underneath, which made the wizard
 *  feel overcrowded even for someone who just wanted the two code snippets. The full README is
 *  now a second, separately-collapsed disclosure — still inline (no popup), just opt-in.
 *  The scope table is generated from WIDGET_API_SCOPES (the same object the host actually checks a
 *  call against — see performScopedFetch), not a separately-typed copy, so it can't drift. */
const WidgetDeveloperGuide: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [showFullGuide, setShowFullGuide] = useState(false);
  const [fullGuide, setFullGuide] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!showFullGuide || fullGuide !== null || error) return;
    const controller = new AbortController();
    fetch(WIDGET_DOCS_URL, { signal: controller.signal })
      .then(res => { if (!res.ok) throw new Error(`Couldn't load the full guide (${res.status})`); return res.text(); })
      .then(t => { if (!controller.signal.aborted) setFullGuide(t); })
      .catch(err => { if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Failed to load'); });
    return () => controller.abort();
  }, [showFullGuide, fullGuide, error]);

  return (
    <div className="wg-guide-wrap">
      <button type="button" className="wg-guide-toggle" onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
        {expanded ? '▾' : '▸'} {expanded ? 'Hide' : 'Read'} the developer guide
      </button>

      {expanded && (
        <div className="wg-guide">
          <div className="wg-guide__quickstart">
            <p className="wg-guide__intro">
              A widget's code runs with no network access at all — reaching any data or outside service goes
              through Momentum AMP, via one of these two calls.
            </p>
            <div className="wg-guide__callout">
              <span className="wg-guide__callout-icon">💡</span>
              <span><code>window.Momentum</code> is injected automatically before your code runs — nothing to
                import or install.</span>
            </div>

            <div className="wg-guide__cards">
              <div className="wg-guide__card">
                <div className="wg-guide__card-head">
                  <span className="wg-guide__card-icon">🗂️</span>
                  <span className="wg-guide__card-title">Momentum AMP's own data</span>
                  <code className="wg-guide__card-fn">Momentum.fetch</code>
                </div>
                <p className="wg-guide__note" style={{ marginTop: 0 }}>
                  Pulls data Momentum AMP already has — policies, opportunities, tasks — using the signed-in
                  person's own access, so a widget can never see more than they can.
                </p>
                <pre><code>{`Momentum.fetch('policies.read', 'renewals', { query: { days_before: 90 } })
  .then(data => /* use it */ null)
  .catch(err => console.log(err.message, err.status));`}</code></pre>
                <p className="wg-guide__verified">
                  <span className="wg-guide__badge wg-guide__badge--ok">✓ Verified</span>
                  Tested end to end against a real agency account.
                </p>
              </div>

              <div className="wg-guide__card">
                <div className="wg-guide__card-head">
                  <span className="wg-guide__card-icon">🔌</span>
                  <span className="wg-guide__card-title">A different company's API</span>
                  <code className="wg-guide__card-fn">Momentum.connect</code>
                </div>
                <p className="wg-guide__note" style={{ marginTop: 0 }}>
                  Calls an outside service using a key the installer supplies once, in the <strong>Third-party
                  API connections</strong> section below — your code never sees it.
                </p>
                <pre><code>{`// "acme" matches a connection key declared below
Momentum.connect('acme', 'contacts/123', { method: 'GET' })
  .then(data => /* use it */ null)
  .catch(err => console.log(err.message, err.status));`}</code></pre>
                <p className="wg-guide__verified">
                  <span className="wg-guide__badge wg-guide__badge--ok">✓ Verified</span>
                  Mechanism tested end to end against a real outside API.
                </p>
              </div>

              <div className="wg-guide__card">
                <div className="wg-guide__card-head">
                  <span className="wg-guide__card-icon">📡</span>
                  <span className="wg-guide__card-title">Live task updates</span>
                  <code className="wg-guide__card-fn">Momentum.subscribe</code>
                </div>
                <p className="wg-guide__note" style={{ marginTop: 0 }}>
                  Gets notified when kanban tasks change, so you can refresh your own data instead of polling.
                  Momentum AMP forwards a bare "something changed" signal, never task details — call
                  <code> Momentum.fetch</code> for those.
                </p>
                <pre><code>{`Momentum.subscribe('kanban', () => {
  // something changed — re-fetch what you need
  Momentum.fetch('kanban.read', 'tasks').then(render);
});`}</code></pre>
              </div>
            </div>

            <h4 className="wg-guide__scopes-h">Every scope <code>Momentum.fetch</code> supports</h4>
            <div className="wg-guide__scope-table">
              <table>
                <thead><tr><th>Scope (after <code>api:</code>)</th><th>What it's for</th><th>Needs AMS?</th><th>What you can call</th></tr></thead>
                <tbody>
                  {Object.entries(WIDGET_API_SCOPES).map(([key, def]) => (
                    <tr key={key}>
                      <td><code>{key}</code></td>
                      <td>{def.description}</td>
                      <td>{def.needsAmsToken ? 'Yes — NowCerts' : '—'}</td>
                      <td>{def.examples.map(ex => <code key={ex} className="wg-guide__route">{ex}</code>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="wg-guide__note">
              Need a scope that's not here? It has to be added to Momentum AMP's own allowlist first — a widget
              can't unlock one itself. Capped at 60 calls/minute.
            </p>
            <p className="wg-guide__note">
              Request and response formats for every route, plus what widgets can't do yet:{' '}
              <a href={WIDGET_ROUTES_DOCS_URL} target="_blank" rel="noopener noreferrer">route reference</a>.
            </p>
          </div>

          <div className="wg-guide__divider" />

          <button type="button" className="wg-guide-toggle" onClick={() => setShowFullGuide(s => !s)} aria-expanded={showFullGuide}>
            {showFullGuide ? '▾ Hide' : '▸ Show'} the full reference (package format, permissions, SDK, everything)
          </button>
          {showFullGuide && (
            <div className="wg-guide__full">
              {error && <p className="wg-listing__muted">{error}</p>}
              {fullGuide === null && !error && <p className="wg-listing__muted">Loading…</p>}
              {fullGuide !== null && (
                <ReactMarkdown components={{
                  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
                  img: () => null,
                }}>
                  {fullGuide}
                </ReactMarkdown>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WidgetDeveloperGuide;
